import classes from "./Landing.module.scss";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ValidUserContext from "../authCheck";
import {
  adminPath,
  dashboardPath,
  matchesView,
  matchesWorkbook,
  parsePortalPath,
} from "../routes";

import menuIcon from "../assets/fa-menu.svg";
import { ArrowRightOnRectangleIcon, ChevronDownIcon } from "./Icons";

import brandLogo from "../assets/CDAnalyticsLogo.png";
import Dashboard from "./Dashboard";
import FolderAdmin from "./FolderAdmin";
import GroupAdmin from "./GroupAdmin";
import UserAdmin from "./UserAdmin";

const VIEW_PILL_LIMIT = 10;
const WORKBOOK_PILL_LIMIT = 10;

const isMobileDevice = () => /Mobi|Android/i.test(navigator.userAgent);

// "AdminInsightsStarter/sheets/UserDrilldown" -> "User Drilldown"
const formatViewLabel = (url) => {
  const seg = url.split("/sheets/")[1] || url.split("/").pop() || url;
  return seg
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
};

const normalizeKey = (value) =>
  (value || "")
    .replace(/\u2013/g, "-")
    .replace(/\u2014/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const loadFolderAliases = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("folder-aliases") || "{}") || {};
    const lookup = {};
    Object.entries(raw).forEach(([key, alias]) => {
      const label = String(alias || "").trim();
      if (!key || !label) return;
      lookup[key] = label;
      lookup[normalizeKey(key)] = label;
    });
    return lookup;
  } catch (error) {
    return {};
  }
};

const loadFolderPriorities = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("folder-priorities") || "{}") || {};
    const lookup = {};
    Object.entries(raw).forEach(([key, priority]) => {
      const number = Number(priority);
      if (!key || !Number.isFinite(number)) return;
      lookup[key] = number;
      lookup[normalizeKey(key)] = number;
    });
    return lookup;
  } catch (error) {
    return {};
  }
};

const sectionPriority = (label, folderId, priorities) => {
  const candidates = [folderId, label, normalizeKey(label)];
  for (const key of candidates) {
    if (key && priorities[key] != null) return priorities[key];
  }
  return Number.POSITIVE_INFINITY;
};

/** Toolbar label: saved alias by folder id (or name), else stripped CDAnalytics prefix */
const formatNavLabel = (name, aliases = {}, folderId = "") => {
  const alias = (folderId && aliases[folderId]) || aliases[name] || aliases[normalizeKey(name)];
  if (alias) return alias;
  return (
    (name || "")
      .replace(/^CDAnalytics\s*[–-]\s*/i, "")
      .trim() || name
  );
};

const buildWorkbookList = (navEntries) =>
  navEntries
    .map(([id, wb]) => ({
      id,
      name: wb.name,
      client: wb.client,
      folderId: wb.folder_id || "",
      dashboards: wb.dashboards || [],
      dashboard_ids: wb.dashboard_ids || [],
      dashboard_names: wb.dashboard_names || [],
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

const buildViews = (workbook) => {
  if (!workbook) return [];
  return (workbook.dashboards || []).map((url, i) => ({
    url,
    id: (workbook.dashboard_ids || [])[i],
    label: (workbook.dashboard_names || [])[i] || formatViewLabel(url),
    workbook: workbook.name,
  }));
};

const selectionFromUrl = (pathname, workbooks) => {
  const parsed = parsePortalPath(pathname);
  if (parsed.kind !== "dashboard") return null;
  const workbook = workbooks.find((wb) =>
    matchesWorkbook(wb, parsed.workbookKey)
  );
  if (!workbook) return null;
  const views = buildViews(workbook);
  let index = parsed.viewKey
    ? views.findIndex((view, i) => matchesView(view, i, parsed.viewKey))
    : 0;
  if (index < 0) index = 0;
  return { workbook, views, index };
};

const firstSelection = (workbooks, contentSections, isAdmin) => {
  let workbook = workbooks[0] || null;
  if (!isAdmin && contentSections[0]) {
    const key = normalizeKey(contentSections[0]);
    workbook =
      workbooks.find((wb) => normalizeKey(wb.client || "Dashboards") === key) ||
      workbook;
  }
  if (!workbook) return null;
  const views = buildViews(workbook);
  return { workbook, views, index: 0 };
};

const pathForSelection = (selection) => {
  if (!selection?.workbook) return "/";
  return dashboardPath(
    selection.workbook,
    selection.views[selection.index],
    selection.index
  );
};

const PAGE_TITLE_SUFFIX = "CDAnalytics";

const documentTitleFor = ({
  showAdminPanel,
  adminTask,
  viewLabel,
}) => {
  if (showAdminPanel) {
    const labels = {
      users: "User Management",
      groups: "Security Groups",
      folders: "Folder Aliases",
    };
    return `${labels[adminTask] || "Admin"} | ${PAGE_TITLE_SUFFIX}`;
  }
  if (viewLabel) return `${viewLabel} | ${PAGE_TITLE_SUFFIX}`;
  return PAGE_TITLE_SUFFIX;
};

const Landing = () => {
  const validUserContext = useContext(ValidUserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardRef = useRef(null);
  const viewPickerRef = useRef(null);
  const workbookPickerRef = useRef(null);
  const clientPickerRef = useRef(null);
  const profileRef = useRef(null);
  const recoveringRef = useRef(false);
  const failedRecoveryRef = useRef(false);

  const navEntries = useMemo(
    () => Object.entries(JSON.parse(localStorage.getItem("navigation")) || {}),
    []
  );
  const group = JSON.parse(localStorage.getItem("group")) ?? "default";
  const isAdmin = group === "Admin";

  const userName = (JSON.parse(localStorage.getItem("user-name")) || "").trim();
  const loginEmail = (localStorage.getItem("login-name") || "").trim();
  const role = JSON.parse(localStorage.getItem("role")) || "";
  const roleLabel = role.includes("Administrator") ? "Administrator" : role;

  const folderAliases = useMemo(() => loadFolderAliases(), []);
  const folderPriorities = useMemo(() => loadFolderPriorities(), []);
  const allWorkbooks = useMemo(() => buildWorkbookList(navEntries), [navEntries]);

  // Non-admin primary options: project/client buckets from navigation.
  const contentSections = useMemo(() => {
    if (isAdmin) return [];
    const byKey = new Map();
    const ids = {};
    allWorkbooks.forEach((wb) => {
      const label = wb.client || "Dashboards";
      const key = normalizeKey(label);
      if (!byKey.has(key)) byKey.set(key, label);
      if (wb.folderId && !ids[key]) ids[key] = wb.folderId;
    });

    const ordered = [...byKey.values()].sort((a, b) => {
      const delta =
        sectionPriority(a, ids[normalizeKey(a)], folderPriorities) -
        sectionPriority(b, ids[normalizeKey(b)], folderPriorities);
      if (delta) return delta;
      return formatNavLabel(a, folderAliases, ids[normalizeKey(a)]).localeCompare(
        formatNavLabel(b, folderAliases, ids[normalizeKey(b)])
      );
    });
    return ordered.length ? ordered : ["Dashboards"];
  }, [isAdmin, allWorkbooks, folderAliases, folderPriorities]);

  const sectionFolderIds = useMemo(() => {
    const ids = {};
    allWorkbooks.forEach((wb) => {
      const key = normalizeKey(wb.client || "Dashboards");
      if (wb.folderId && !ids[key]) ids[key] = wb.folderId;
    });
    return ids;
  }, [allWorkbooks]);

  const bootParsed = parsePortalPath(location.pathname);
  const bootSelection =
    selectionFromUrl(location.pathname, allWorkbooks) ||
    firstSelection(allWorkbooks, contentSections, isAdmin);
  const bootViews = bootSelection?.views || [];
  const bootIndex = bootSelection?.index || 0;

  const [primarySection, setPrimarySection] = useState(() =>
    isAdmin ? (bootParsed.kind === "admin" ? "admin" : "dashboards") : null
  );
  const [adminTask, setAdminTask] = useState(
    bootParsed.kind === "admin" ? bootParsed.task : "users"
  );
  const [selectedContentSection, setSelectedContentSection] = useState(
    bootSelection?.workbook?.client || contentSections[0] || ""
  );

  const activePrimary = isAdmin
    ? primarySection
    : selectedContentSection || contentSections[0] || "";

  const scopedWorkbooks = useMemo(() => {
    if (isAdmin) return allWorkbooks;
    const key = normalizeKey(activePrimary);
    const filtered = allWorkbooks.filter(
      (wb) => normalizeKey(wb.client || "Dashboards") === key
    );
    return filtered.length ? filtered : allWorkbooks;
  }, [isAdmin, allWorkbooks, activePrimary]);

  const [selectedWorkbookId, setSelectedWorkbookId] = useState(
    bootSelection?.workbook?.id || ""
  );

  const selectedWorkbook = useMemo(
    () =>
      allWorkbooks.find((wb) => wb.id === selectedWorkbookId) ||
      scopedWorkbooks.find((wb) => wb.id === selectedWorkbookId) ||
      scopedWorkbooks[0] ||
      null,
    [allWorkbooks, scopedWorkbooks, selectedWorkbookId]
  );

  const [views, setViews] = useState(bootViews);
  const [activeIndex, setActiveIndex] = useState(bootIndex);
  const [activeURL, setActiveURL] = useState(bootViews[bootIndex]?.url || "");
  const [embedURL, setEmbedURL] = useState(bootViews[0]?.url || "");
  const [navOpen, setNavOpen] = useState(!isMobileDevice());
  const [profileOpen, setProfileOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [workbookMenuOpen, setWorkbookMenuOpen] = useState(false);
  const [clientMenuOpen, setClientMenuOpen] = useState(false);
  const [vizEpoch, setVizEpoch] = useState(0);

  const showAdminArea = isAdmin && primarySection === "admin";
  const showUsers = showAdminArea && adminTask === "users";
  const showGroups = showAdminArea && adminTask === "groups";
  const showFolders = showAdminArea && adminTask === "folders";
  const showAdminPanel = showUsers || showGroups || showFolders;
  const showDashboardChrome = !showAdminPanel;
  const useViewPicker = views.length > VIEW_PILL_LIMIT;
  const useWorkbookPicker = scopedWorkbooks.length > WORKBOOK_PILL_LIMIT;
  const activeView = views[activeIndex] || null;

  useEffect(() => {
    document.title = documentTitleFor({
      showAdminPanel,
      adminTask,
      viewLabel: activeView?.label,
    });
    return () => {
      document.title = PAGE_TITLE_SUFFIX;
    };
  }, [showAdminPanel, adminTask, activeView?.label]);

  // Close menus on outside click or Escape
  useEffect(() => {
    if (!viewMenuOpen && !workbookMenuOpen && !clientMenuOpen && !profileOpen) return undefined;

    const onPointerDown = (e) => {
      if (
        viewMenuOpen &&
        viewPickerRef.current &&
        !viewPickerRef.current.contains(e.target)
      ) {
        setViewMenuOpen(false);
      }
      if (
        workbookMenuOpen &&
        workbookPickerRef.current &&
        !workbookPickerRef.current.contains(e.target)
      ) {
        setWorkbookMenuOpen(false);
      }
      if (
        clientMenuOpen &&
        clientPickerRef.current &&
        !clientPickerRef.current.contains(e.target)
      ) {
        setClientMenuOpen(false);
      }
      if (
        profileOpen &&
        profileRef.current &&
        !profileRef.current.contains(e.target)
      ) {
        setProfileOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setViewMenuOpen(false);
        setWorkbookMenuOpen(false);
        setClientMenuOpen(false);
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [viewMenuOpen, workbookMenuOpen, clientMenuOpen, profileOpen]);

  useEffect(() => {
    if (!viewMenuOpen) return;
    const active = viewPickerRef.current?.querySelector('[aria-selected="true"]');
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [viewMenuOpen, activeIndex]);

  useEffect(() => {
    if (!workbookMenuOpen) return;
    const active = workbookPickerRef.current?.querySelector('[aria-selected="true"]');
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [workbookMenuOpen, selectedWorkbookId]);

  useEffect(() => {
    if (!clientMenuOpen) return;
    const active = clientPickerRef.current?.querySelector('[aria-selected="true"]');
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [clientMenuOpen, activePrimary]);

  useEffect(() => {
    const parsed = parsePortalPath(location.pathname);

    const goToFallback = () => {
      const fallback = firstSelection(allWorkbooks, contentSections, isAdmin);
      if (fallback) {
        navigate(pathForSelection(fallback), { replace: true });
      } else if (isAdmin) {
        navigate(adminPath("users"), { replace: true });
      } else if (location.pathname !== "/") {
        navigate("/", { replace: true });
      }
    };

    if (parsed.kind === "admin") {
      if (!isAdmin) {
        goToFallback();
        return;
      }
      setPrimarySection("admin");
      setAdminTask(parsed.task);
      const canonical = adminPath(parsed.task);
      if (location.pathname !== canonical) {
        navigate(canonical, { replace: true });
      }
      return;
    }

    if (parsed.kind === "dashboard") {
      const selection = selectionFromUrl(location.pathname, allWorkbooks);
      if (!selection) {
        goToFallback();
        return;
      }
      const { workbook, views: nextViews, index } = selection;
      setSelectedWorkbookId(workbook.id || "");
      setViews(nextViews);
      setActiveIndex(index);
      setActiveURL(nextViews[index]?.url || "");
      setEmbedURL(nextViews[0]?.url || "");
      if (!isAdmin) {
        setSelectedContentSection(workbook.client || contentSections[0] || "");
      } else {
        setPrimarySection("dashboards");
      }
      const canonical = pathForSelection(selection);
      if (location.pathname !== canonical) {
        navigate(canonical, { replace: true });
      }
      return;
    }

    goToFallback();
  }, [
    location.pathname,
    isAdmin,
    allWorkbooks,
    contentSections,
    navigate,
  ]);

  const navigateIfChanged = (path) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
  };

  const handleWorkbookChange = (workbookId) => {
    const workbook = allWorkbooks.find((wb) => wb.id === workbookId);
    const nextViews = buildViews(workbook);
    navigateIfChanged(dashboardPath(workbook, nextViews[0], 0));
    setViewMenuOpen(false);
    setWorkbookMenuOpen(false);
  };

  const handleViewClick = (index) => {
    validUserContext.localAuthCheck(false);
    if (selectedWorkbook) {
      navigateIfChanged(dashboardPath(selectedWorkbook, views[index], index));
    }
    setViewMenuOpen(false);
    if (isMobileDevice()) setNavOpen(false);
  };

  const selectAdmin = () => {
    navigateIfChanged(adminPath(adminTask || "users"));
  };

  const selectDashboards = () => {
    if (selectedWorkbook) {
      navigateIfChanged(
        dashboardPath(selectedWorkbook, views[activeIndex], activeIndex)
      );
      return;
    }
    const fallback = firstSelection(allWorkbooks, contentSections, isAdmin);
    if (fallback) {
      navigateIfChanged(pathForSelection(fallback));
    }
  };

  const selectContentSection = (section) => {
    setClientMenuOpen(false);
    const key = normalizeKey(section);
    const workbook =
      allWorkbooks.find(
        (wb) => normalizeKey(wb.client || "Dashboards") === key
      ) || allWorkbooks[0];
    const nextViews = buildViews(workbook);
    if (workbook) {
      navigateIfChanged(dashboardPath(workbook, nextViews[0], 0));
    }
  };

  const getViz = () => dashboardRef.current?.querySelector("tableau-viz");

  const handleExportExcelClick = async () => {
    const viz = getViz();
    if (!viz) return;
    try {
      await viz.displayDialogAsync("export-cross-tab");
    } catch (err) {
      console.error("Excel export failed", err);
    }
  };

  const handleExportPDFClick = async () => {
    const viz = getViz();
    if (!viz) return;
    try {
      await viz.displayDialogAsync("export-pdf");
    } catch (err) {
      console.error("PDF export failed", err);
    }
  };

  const handleExportPowerPointClick = async () => {
    const viz = getViz();
    if (!viz) return;
    try {
      await viz.displayDialogAsync("export-powerpoint");
    } catch (err) {
      console.error("PowerPoint export failed", err);
    }
  };

  const handleExportImageClick = async () => {
    const viz = getViz();
    if (!viz) return;
    try {
      await viz.exportImageAsync();
    } catch (err) {
      console.error("Image export failed", err);
    }
  };

  const handleVizReady = () => {
    failedRecoveryRef.current = false;
  };

  const handleSessionExpired = async (reason) => {
    if (recoveringRef.current) return;
    if (failedRecoveryRef.current) {
      validUserContext.logoutUser(reason || "viz_recovery_failed");
      return;
    }
    recoveringRef.current = true;
    failedRecoveryRef.current = true;
    try {
      const ok = await validUserContext.refreshTableauEmbedToken();
      if (ok) {
        setVizEpoch((epoch) => epoch + 1);
      }
    } finally {
      recoveringRef.current = false;
    }
  };

  const handleLogout = () => validUserContext.logoutUser("explicit");

  const handleExitImpersonation = async () => {
    try {
      await validUserContext.exitImpersonation();
    } catch (err) {
      // Keep impersonation state if restore fails so the admin can retry.
      // eslint-disable-next-line no-alert
      window.alert(err.message || "Could not exit impersonation");
    }
  };

  const renderWorkbookPills = () => {
    if (!showDashboardChrome || scopedWorkbooks.length === 0) return null;

    if (!useWorkbookPicker) {
      return (
        <div className={classes.workbookNav} role="tablist" aria-label="Workbooks">
          {scopedWorkbooks.map((wb) => (
            <button
              key={wb.id}
              type="button"
              role="tab"
              aria-selected={wb.id === selectedWorkbook?.id}
              className={`${classes.workbookPill} ${
                wb.id === selectedWorkbook?.id ? classes.workbookPillActive : ""
              }`}
              onClick={() => handleWorkbookChange(wb.id)}
              title={wb.name}
            >
              {wb.name}
            </button>
          ))}
        </div>
      );
    }

    if (!selectedWorkbook) return null;

    return (
      <div className={classes.workbookPicker} ref={workbookPickerRef}>
        <button
          type="button"
          className={`${classes.workbookPill} ${classes.workbookPillActive} ${classes.workbookPickerButton}`}
          onClick={() => setWorkbookMenuOpen((o) => !o)}
          aria-expanded={workbookMenuOpen}
          aria-haspopup="listbox"
          title={selectedWorkbook.name}
        >
          <span className={classes.workbookPickerLabel}>{selectedWorkbook.name}</span>
          <ChevronDownIcon className={classes.chevron} />
        </button>
        {workbookMenuOpen && (
          <div className={classes.pickerMenu} role="listbox">
            {scopedWorkbooks.map((wb) => (
              <button
                key={wb.id}
                type="button"
                role="option"
                aria-selected={wb.id === selectedWorkbook?.id}
                className={`${classes.pickerItem} ${
                  wb.id === selectedWorkbook?.id ? classes.pickerItemActive : ""
                }`}
                onClick={() => handleWorkbookChange(wb.id)}
                title={wb.name}
              >
                {wb.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.app}>
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          <button
            className={classes.menuToggle}
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Toggle navigation"
          >
            <img src={menuIcon} alt="Menu" />
          </button>
          <img className={classes.brandLogo} src={brandLogo} alt="CD Analytics" />
        </div>

        <div className={classes.headerCenter}>
          {isAdmin ? (
            <>
              <div className={classes.sectionNav} role="tablist" aria-label="Sections">
                <button
                  type="button"
                  role="tab"
                  aria-selected={primarySection === "admin"}
                  className={`${classes.workbookPill} ${
                    primarySection === "admin" ? classes.workbookPillActive : ""
                  }`}
                  onClick={selectAdmin}
                >
                  Admin
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={primarySection === "dashboards"}
                  className={`${classes.workbookPill} ${
                    primarySection === "dashboards" ? classes.workbookPillActive : ""
                  }`}
                  onClick={selectDashboards}
                >
                  Dashboards
                </button>
              </div>

              {primarySection === "admin" && (
                <div className={classes.workbookNav} role="tablist" aria-label="Admin tasks">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminTask === "users"}
                    className={`${classes.workbookPill} ${
                      adminTask === "users" ? classes.workbookPillActive : ""
                    }`}
                    onClick={() => navigateIfChanged(adminPath("users"))}
                  >
                    User Management
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminTask === "groups"}
                    className={`${classes.workbookPill} ${
                      adminTask === "groups" ? classes.workbookPillActive : ""
                    }`}
                    onClick={() => navigateIfChanged(adminPath("groups"))}
                  >
                    Security Groups
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminTask === "folders"}
                    className={`${classes.workbookPill} ${
                      adminTask === "folders" ? classes.workbookPillActive : ""
                    }`}
                    onClick={() => navigateIfChanged(adminPath("folders"))}
                  >
                    Folder Aliases
                  </button>
                </div>
              )}

              {primarySection === "dashboards" && renderWorkbookPills()}
            </>
          ) : (
            <>
              {contentSections.length > 1 && (
                <div className={classes.sectionNav}>
                  <div className={classes.workbookPicker} ref={clientPickerRef}>
                    <button
                      type="button"
                      className={`${classes.workbookPill} ${classes.workbookPillActive} ${classes.workbookPickerButton}`}
                      onClick={() => setClientMenuOpen((open) => !open)}
                      aria-expanded={clientMenuOpen}
                      aria-haspopup="listbox"
                      aria-label="Clients"
                      title={formatNavLabel(
                        activePrimary,
                        folderAliases,
                        sectionFolderIds[normalizeKey(activePrimary)]
                      )}
                    >
                      <span className={classes.workbookPickerLabel}>
                        {formatNavLabel(
                          activePrimary,
                          folderAliases,
                          sectionFolderIds[normalizeKey(activePrimary)]
                        )}
                      </span>
                      <ChevronDownIcon className={classes.chevron} />
                    </button>
                    {clientMenuOpen && (
                      <div className={classes.pickerMenu} role="listbox">
                        {contentSections.map((section) => {
                          const selected =
                            normalizeKey(activePrimary) === normalizeKey(section);
                          const label = formatNavLabel(
                            section,
                            folderAliases,
                            sectionFolderIds[normalizeKey(section)]
                          );
                          return (
                            <button
                              key={section}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={`${classes.pickerItem} ${
                                selected ? classes.pickerItemActive : ""
                              }`}
                              onClick={() => selectContentSection(section)}
                              title={label}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {renderWorkbookPills()}
            </>
          )}
        </div>

        <div className={classes.headerRight}>
          <div className={classes.profile} ref={profileRef}>
            <button
              className={classes.profileButton}
              onClick={() => setProfileOpen((o) => !o)}
            >
              <span className={classes.profileText}>
                <span className={classes.profileName}>{userName || "Account"}</span>
                {(loginEmail || roleLabel) && (
                  <span className={classes.profileRole}>
                    {loginEmail || roleLabel}
                  </span>
                )}
              </span>
            </button>
            {profileOpen && (
              <div className={classes.profileMenu}>
                <button onClick={handleLogout}>
                  <ArrowRightOnRectangleIcon /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {validUserContext.isImpersonating && (
        <div className={classes.impersonationBar} role="status">
          <span>
            Viewing as{" "}
            <strong>
              {validUserContext.impersonationTarget ||
                localStorage.getItem("login-name") ||
                "user"}
            </strong>
          </span>
          <button
            type="button"
            className={classes.impersonationExit}
            onClick={handleExitImpersonation}
          >
            Exit impersonation
          </button>
        </div>
      )}

      {showDashboardChrome && (
        <nav className={`${classes.subnav} ${navOpen ? "" : classes.subnavClosed}`}>
          <div
            className={`${classes.viewNav} ${useViewPicker ? classes.viewNavPicker : ""}`}
          >
            {views.length === 0 && (
              <span className={classes.noViews}>No dashboards available</span>
            )}

            {!useViewPicker &&
              views.map((view, index) => (
                <button
                  key={view.id || index}
                  type="button"
                  className={`${classes.viewTab} ${
                    activeIndex === index ? classes.viewTabActive : ""
                  }`}
                  onClick={() => handleViewClick(index)}
                  title={view.label}
                >
                  {view.label}
                </button>
              ))}

            {useViewPicker && activeView && (
              <div className={classes.viewPicker} ref={viewPickerRef}>
                <button
                  type="button"
                  className={`${classes.viewTab} ${classes.viewTabActive} ${classes.viewPickerButton}`}
                  onClick={() => setViewMenuOpen((o) => !o)}
                  aria-expanded={viewMenuOpen}
                  aria-haspopup="listbox"
                  title={activeView.label}
                >
                  <span className={classes.viewPickerLabel}>{activeView.label}</span>
                  <ChevronDownIcon className={classes.chevron} />
                </button>

                {viewMenuOpen && (
                  <div className={classes.pickerMenu} role="listbox">
                    {views.map((view, index) => (
                      <button
                        key={view.id || index}
                        type="button"
                        role="option"
                        aria-selected={index === activeIndex}
                        className={`${classes.pickerItem} ${
                          index === activeIndex ? classes.pickerItemActive : ""
                        }`}
                        onClick={() => handleViewClick(index)}
                        title={view.label}
                      >
                        {view.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={classes.toolbarActions}>
            <span className={classes.downloadLabel}>Download:</span>
            <button
              type="button"
              className={classes.downloadLink}
              onClick={handleExportExcelClick}
            >
              Excel
            </button>
            <button
              type="button"
              className={classes.downloadLink}
              onClick={handleExportImageClick}
            >
              Image
            </button>
            <button
              type="button"
              className={classes.downloadLink}
              onClick={handleExportPDFClick}
            >
              PDF
            </button>
            <button
              type="button"
              className={classes.downloadLink}
              onClick={handleExportPowerPointClick}
            >
              PowerPoint
            </button>
          </div>
        </nav>
      )}

      <main className={classes.content}>
        {/* Kept mounted while the admin panel is open: Dashboard redeems the
            single-use embed token on mount, so remounting yields a blank viz. */}
        <div
          className={`${classes.dashboardCard} ${showAdminPanel ? classes.hidden : ""}`}
          ref={dashboardRef}
        >
          {embedURL ? (
            <Dashboard
              key={vizEpoch}
              embedUrl={embedURL}
              activeSheetUrl={activeURL}
              displayTabs={false}
              isVisible={!showAdminPanel}
              onSessionExpired={handleSessionExpired}
              onVizReady={handleVizReady}
            />
          ) : (
            <div className={classes.emptyState}>Select a dashboard to get started.</div>
          )}
        </div>

        {showUsers && <UserAdmin />}
        {showGroups && <GroupAdmin />}
        {showFolders && <FolderAdmin />}
      </main>
    </div>
  );
};

export default Landing;
